import React, { useEffect, useRef } from 'react';
import { Instagram } from 'lucide-react';
import { FuturisticBG } from './FuturisticBG';
import { MovingBG } from './MovingBG';
import { gsap, ScrollTrigger } from '../lib/gsap';

// Track Contact event for Meta Pixel
const trackWhatsAppClick = () => {
  if (typeof window.fbq === 'function') {
    const eventId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    window.fbq('track', 'Contact', { 
      content_name: 'WhatsApp Chat',
      content_category: 'Contact Section',
      eventID: eventId
    });
    console.log('[Meta Pixel] Contact tracked from Contact Section', { eventId });
  }
};

const team = [
  { name: 'Luqmanan', role: 'Founder',              image: 'https://tafworldwide.b-cdn.net/team/luqmanan_founder.jpg' },
  { name: 'Ija',      role: 'Co-Founder',            image: 'https://customer-assets.emergentagent.com/job_taf-studios/artifacts/p2ggiigy_IMG_4941.png' },
  { name: 'Tenuk Film', role: 'Operation Manager',   image: 'https://customer-assets.emergentagent.com/job_taf-studios/artifacts/heaciu0t_IMG_2522.jpg' },
  { name: 'Airiz',    role: 'Business Development',  image: 'https://customer-assets.emergentagent.com/job_taf-studios/artifacts/lubwxty7_IMG_9342.jpg' },
];

const ProfileCard = ({ member }) => (
  <div className="group relative flex flex-col rounded-2xl border border-white/8 hover:border-orange-500/40 transition-all duration-500">
    <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-orange-500/60 rounded-tl-2xl z-10 pointer-events-none" />
    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-orange-500/60 rounded-br-2xl z-10 pointer-events-none" />
    <div className="relative overflow-hidden rounded-t-2xl aspect-[4/5] sm:aspect-[3/4] md:aspect-[4/5] lg:aspect-[5/6]">
      <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-orange-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </div>
    <div className="p-2.5 sm:p-3 md:p-4 rounded-b-2xl" style={{ background: 'rgba(5,5,5,0.85)' }}>
      <p className="text-[8px] sm:text-[9px] md:text-[10px] font-bold uppercase tracking-[0.22em] text-orange-400 mb-0.5">{member.role}</p>
      <h3 className="text-xs sm:text-sm md:text-base font-black text-white leading-tight tracking-tight">{member.name}</h3>
    </div>
  </div>
);

export const Contact = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const ctx = gsap.context(() => {
      const q = gsap.utils.selector(sectionRef);

      // ── "Meet The Team" heading slides from RIGHT ──
      gsap.from(q('[data-gs="team-heading"]'), {
        x: 60,
        scrollTrigger: {
          trigger: q('[data-gs="team-heading"]')[0],
          start: 'top 85%',
          end: 'top 60%',
          scrub: 0.5,
        },
      });

      // ── Team row 1: left card from LEFT, right card from RIGHT ──
      gsap.from(q('[data-gs="team-r1-l"]'), {
        x: -60,
        scrollTrigger: {
          trigger: q('[data-gs="team-r1-l"]')[0],
          start: 'top 88%',
          end: 'top 55%',
          scrub: 0.5,
        },
      });

      gsap.from(q('[data-gs="team-r1-r"]'), {
        x: 60,
        scrollTrigger: {
          trigger: q('[data-gs="team-r1-r"]')[0],
          start: 'top 88%',
          end: 'top 55%',
          scrub: 0.5,
        },
      });

      // ── Team row 2: stagger rise ──
      const row2 = q('[data-gs="team-r2"]');
      gsap.from(row2, {
        y: 40,
        stagger: 0.08,
        scrollTrigger: {
          trigger: row2[0],
          start: 'top 90%',
          end: 'top 58%',
          scrub: 0.5,
        },
      });

      // ── Contact info box rises ──
      gsap.from(q('[data-gs="contact-box"]'), {
        y: 35,
        scale: 0.97,
        scrollTrigger: {
          trigger: q('[data-gs="contact-box"]')[0],
          start: 'top 90%',
          end: 'top 60%',
          scrub: 0.5,
        },
      });

      // ── Instagram CTA slides from bottom ──
      gsap.from(q('[data-gs="insta-cta"]'), {
        y: 45,
        scale: 0.97,
        scrollTrigger: {
          trigger: q('[data-gs="insta-cta"]')[0],
          start: 'top 92%',
          end: 'top 62%',
          scrub: 0.5,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="contact" ref={sectionRef} className="py-20 relative overflow-hidden" style={{ background: '#050505' }}>
      <MovingBG />
      <FuturisticBG density={10} />

      {/* Geometric background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, #f97316 0px, #f97316 1px, transparent 0px, transparent 50%)',
          backgroundSize: '40px 40px'
        }} />
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-orange-600/12 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-amber-600/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/20 to-transparent" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Header */}
        <div data-gs="team-heading" className="text-center mb-12 will-change-transform">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-orange-500 mb-3">TAF WORLDWIDE</p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 tracking-tight">
            <span className="text-white">Meet </span>
            <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">The Team</span>
          </h2>
          <div className="flex items-center justify-center gap-3 mt-5">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-orange-500" />
            <div className="w-1.5 h-1.5 bg-orange-500 rounded-full" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-orange-500" />
          </div>
        </div>

        {/* Team Grid - Compact on desktop, full on mobile */}
        <div className="space-y-3 sm:space-y-4 max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl mx-auto">
          {/* Row 1 */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
            <div data-gs="team-r1-l" className="will-change-transform">
              <ProfileCard member={team[0]} />
            </div>
            <div data-gs="team-r1-r" className="will-change-transform">
              <ProfileCard member={team[1]} />
            </div>
          </div>
          {/* Row 2 */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
            {[team[2], team[3]].map((m) => (
              <div key={m.name} data-gs="team-r2" className="will-change-transform">
                <ProfileCard member={m} />
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 my-14">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent to-orange-500/30" />
          <div className="w-2 h-2 bg-orange-500 rounded-full" />
          <div className="flex-1 h-px bg-gradient-to-l from-transparent to-orange-500/30" />
        </div>

        {/* Contact box — WhatsApp CTA fokus */}
        <div data-gs="contact-box" className="mb-12 will-change-transform">
          <div className="max-w-lg mx-auto text-center">
            <p className="text-orange-500 text-[10px] uppercase tracking-[0.35em] mb-4 font-bold">Ready to start?</p>
            <h3 className="text-2xl sm:text-3xl font-black text-white mb-3" style={{ fontFamily: "'TafHeadline'", letterSpacing: '0.03em' }}>
              Let's Create Something Amazing
            </h3>
            <p className="text-gray-400 text-sm mb-8">
              Chat with our team directly. We'll discuss your project and get things moving.
            </p>

            <a
              href="https://wa.me/601133131844?text=Hi%20TAF%20Worldwide!%20I%20am%20interested%20in%20your%20cinematography%20services."
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackWhatsAppClick}
              data-testid="contact-whatsapp-cta"
              className="inline-flex items-center justify-center gap-3 w-full sm:w-auto px-12 py-5 text-white font-bold text-lg rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-green-500/25 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
              Message Us on WhatsApp
            </a>

            <div className="flex items-center justify-center gap-6 mt-6 text-sm text-gray-500">
              <a href="mailto:admin@tafworldwide.com" className="hover:text-orange-400 transition-colors" data-testid="contact-email">
                admin@tafworldwide.com
              </a>
              <span className="text-white/10">|</span>
              <a href="tel:+601133131844" className="hover:text-orange-400 transition-colors" data-testid="contact-phone">
                +60 11-3313 1844
              </a>
            </div>
          </div>
        </div>

        {/* Instagram CTA */}
        <div data-gs="insta-cta" className="will-change-transform">
          <div className="relative rounded-3xl overflow-hidden border border-orange-500/20 p-6 sm:p-8 md:p-10"
            style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.08) 0%, rgba(0,0,0,0) 60%)' }}>
            <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 border-t-2 border-r-2 border-orange-500/30 rounded-tr-3xl" />
            <div className="absolute bottom-0 left-0 w-24 h-24 sm:w-32 sm:h-32 border-b-2 border-l-2 border-orange-500/30 rounded-bl-3xl" />
            <div className="text-center mb-6 relative z-10">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-white mb-2 tracking-tight">
                Follow Us on{' '}
                <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">Instagram</span>
              </h3>
              <p className="text-gray-400 text-sm">Watch all our latest cinematography work</p>
            </div>
            <div className="grid grid-cols-2 gap-2 max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg mx-auto mb-6 relative z-10">
              <div className="group overflow-hidden rounded-xl border border-white/10 hover:border-orange-500/40 transition-colors duration-300">
                <img src="https://customer-assets.emergentagent.com/job_5469ee65-28a6-4946-b9a0-e6b0ef46ff90/artifacts/q8cu9wjy_IMG_0802.jpg" alt="TAF Instagram" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="group overflow-hidden rounded-xl border border-white/10 hover:border-orange-500/40 transition-colors duration-300">
                <img src="https://customer-assets.emergentagent.com/job_5469ee65-28a6-4946-b9a0-e6b0ef46ff90/artifacts/7h4txf6l_IMG_0803.JPG" alt="TAF Instagram Stats" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            </div>
            <div className="text-center relative z-10">
              <a href="https://www.instagram.com/taf.worldwide?igsh=MXZwaGNvNmxja2k4Mg==" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white font-bold text-sm sm:text-base rounded-full transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/30"
                data-testid="instagram-cta-button">
                <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />
                @taf.worldwide
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
